import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, CaretDown, Crown, Lock, Paperclip, Scroll, UsersThree, Vault,
} from "@phosphor-icons/react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { fmtPeriod } from "@/lib/period";
import { useAuth } from "@/context/AuthContext";
import { ConfBadge } from "@/components/shared"; // eslint-disable-line no-unused-vars
import { Ring } from "@/components/frameworkBits";
import { FrameworkSwitcher } from "@/components/FrameworkSwitcher";
import { toast } from "sonner";

const IMAGES = {
  "family-charter": "https://static.prod-images.emergentagent.com/jobs/24264fa2-7a69-43f6-a3b1-a4639cedfc7e/images/2b1ce1d0d3a2bd5ae2196b35455c5ee05de977201d83e6627717e2eb7bcd204f.jpeg",
  "wealth-allocation": "https://static.prod-images.emergentagent.com/jobs/24264fa2-7a69-43f6-a3b1-a4639cedfc7e/images/bada4de5aa043e29ff28d65a59395e43c28d03f059cd9b954ceba30911b368ec.jpeg",
  "succession-kartha": "https://static.prod-images.emergentagent.com/jobs/24264fa2-7a69-43f6-a3b1-a4639cedfc7e/images/8cd5284aa0e79ec33643a8bdb14af7cbeab9358aab4f21edc2bc267cf7187f41.jpeg",
};

const PILLARS = [
  {
    key: "family-charter",
    icon: Scroll,
    title: "Family charter",
    desc: "Define family values, vision & purpose",
    to: "/viveka-kartha/family-charter",
    cta: "Go to family charter",
    chip: "bg-[hsl(310_44%_42%)] text-white",
    btn: "bg-[hsl(310_44%_42%)] hover:bg-[hsl(310_46%_34%)]",
    docs: "family_charter",
  },
  {
    key: "wealth-allocation",
    icon: Vault,
    title: "Wealth allocation",
    desc: "Align wealth across safety, market and aspirational buckets",
    to: "/viveka-kartha/wealth",
    cta: "Go to wealth allocation",
    chip: "bg-plum text-white",
    btn: "bg-plum hover:bg-plum/90",
    docs: "wealth_allocation",
  },
  {
    key: "succession-kartha",
    icon: Crown,
    title: "Succession & Kartha accountability",
    desc: "Plan leadership continuity across generations. Define Kartha (Chairman) stewardship & accountability",
    to: "/viveka-kartha/legacy?section=commitments",
    cta: "Go to commitments",
    chip: "bg-[hsl(266_48%_44%)] text-white",
    btn: "bg-[hsl(266_48%_44%)] hover:bg-[hsl(266_50%_36%)]",
    docs: "succession_kartha",
  },
];

const DocPanel = ({ scopeKey, testid }) => {
  const { tenantId } = useAuth();
  const [files, setFiles] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/tenants/${tenantId}/attachments`, {
        params: { scope_type: "pillar_doc", scope_key: `viveka_kartha:${scopeKey}`, framework: "viveka_kartha" },
      });
      setFiles(res.data);
    } catch { setFiles([]); }
  }, [tenantId, scopeKey]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    const form = new FormData();
    form.append("scope_type", "pillar_doc");
    form.append("scope_key", `viveka_kartha:${scopeKey}`);
    form.append("framework", "viveka_kartha");
    form.append("file", file);
    try {
      await api.post(`/tenants/${tenantId}/attachments`, form);
      toast.success("Document attached as a new version");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };


  return (
    <div className="mt-4 border-t border-border pt-4" data-testid={testid}>
      <button onClick={() => setOpen((v) => !v)} data-testid={`${testid}-toggle`}
              className="flex w-full items-center justify-between gap-2 text-[10px] uppercase tracking-[0.14em] text-muted-foreground hover:text-navy">
        <span className="flex items-center gap-2"><Paperclip size={13} /> Documents ({files.length})</span>
        <CaretDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-2 text-left">
          {files.map((f) => (
            <a key={f.id} href={fileUrl(`/tenants/${tenantId}/attachments/${f.id}/download`)}
               target="_blank" rel="noreferrer" data-testid={`${testid}-file-${f.id}`}
               className="block truncate text-xs text-deepblue hover:underline">
              <span className="font-mono text-[10px] text-muted-foreground">v{f.version}</span> {f.filename}
            </a>
          ))}
          {files.length === 0 && <p className="text-xs text-muted-foreground">No document attached yet.</p>}
          <label className="mt-1 block cursor-pointer text-[10px] uppercase tracking-[0.14em] text-navy hover:underline">
            <input type="file" className="hidden" data-testid={`${testid}-input`}
                   onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
            Attach document
          </label>
        </div>
      )}
    </div>
  );
};

export default function VivekaKartha() {
  const { access, tenantId } = useAuth();
  const [score, setScore] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState(null);

  useEffect(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/scorecard`, { params: period ? { period } : {} })
      .then(({ data }) => {
        setScore(data.frameworks?.viveka_kartha || null);
        setPeriods(data.periods || []);
        setPeriod((prev) => prev || data.selected_period || null);
      })
      .catch(() => setScore(null));
  }, [tenantId, period]);

  return (
    <div className="space-y-8" data-testid="vk-overview">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <FrameworkSwitcher current="viveka_kartha" />
        {periods.length > 0 && (
          <select
            data-testid="vk-period-select"
            value={period || ""}
            onChange={(e) => setPeriod(e.target.value)}
            className="h-10 rounded-full border border-border bg-card px-5 text-xs text-navy transition-colors hover:border-plum"
          >
            {periods.map((p) => <option key={p} value={p}>{fmtPeriod(p)}</option>)}
          </select>
        )}
      </div>
      <div className="flex items-center justify-between gap-4">
        <Link to="/" data-testid="vk-back-home"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-navy hover:text-deepblue">
          <span className="grid h-7 w-7 place-items-center rounded-full bg-navy text-white"><ArrowLeft size={13} /></span>
          Back to home
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="panel overflow-hidden">
          <div className="brand-bar h-1.5 w-full" />
          <div className="flex flex-col gap-6 p-6 md:flex-row md:items-start md:p-8">
            <div className="flex items-start gap-5 md:border-r md:border-border md:pr-8">
              <div className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-navy text-white">
                <UsersThree size={30} />
              </div>
              <div>
                <h1 className="font-display text-3xl tracking-tight text-navy sm:text-4xl">Viveka Kartha</h1>
                <p className="mt-1 card-quote">Transition today. Legacy forever.</p>
              </div>
            </div>
            <div className="max-w-md">
              <p className="font-display text-xl leading-snug text-navy">Build legacy. Sustain generations.</p>
              <p className="mt-2 text-sm text-foreground/80">
                Create harmony between family, ownership and business. Build structures that protect values, wealth and
                vision for generations.
              </p>
            </div>
          </div>
        </div>

        <div className="panel flex items-center gap-6 p-6 md:p-8" data-testid="vk-legacy-score-card">
          <Ring value={score?.score10} tone="stroke-plum" testid="vk-legacy-ring">
            <span className="font-mono text-lg tabular-nums text-navy">{score?.score10 ?? "···"}</span>
          </Ring>
          <div>
            <p className="label-caps">Overall legacy score</p>
            <p className="font-mono text-4xl font-semibold tabular-nums text-navy" data-testid="vk-legacy-score">
              {score?.score10 ?? "···"}<span className="text-xl font-semibold text-[hsl(230_28%_30%)]"> /10</span>
            </p>
            <p className="card-body">
              {score?.period_label
                ? `${score.band} · ${fmtPeriod(score.period_label)}`
                : "Complete the legacy survey to score"}
            </p>
            <Link
              to="/viveka-kartha/legacy"
              data-testid="vk-goto-survey"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-[10px] uppercase tracking-[0.16em] text-white transition-colors hover:bg-navy/90"
            >
              Go to Legacy System
              <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4" aria-hidden>
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-plum/50 to-plum/25" />
        <span className="text-plum">✦</span>
        <div className="h-px flex-1 bg-gradient-to-l from-transparent via-plum/50 to-plum/25" />
      </div>

      <div className="stagger grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-6">
        {PILLARS.map((p) => (
          <div key={p.key} className="panel panel-hover flex h-full flex-col overflow-hidden"
               data-testid={`vk-pillar-${p.key}`}>
            <div className="relative h-44 overflow-hidden bg-[hsl(42_60%_97%)]">
              <img src={IMAGES[p.key]} alt={p.title}
                   className="h-full w-full object-contain p-3 transition-transform duration-700 hover:scale-105" />
            </div>
            <div className="flex flex-1 flex-col p-6 text-center">
              <div className="flex items-center justify-center gap-3">
                <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${p.chip}`}>
                  <p.icon size={16} />
                </span>
                <h3 className="text-base font-bold uppercase leading-snug tracking-[0.06em] text-navy md:text-lg">{p.title}</h3>
              </div>
              <div className="mx-auto mt-4 flex w-full items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs text-plum">✦</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <p className="mt-4 flex-1 card-body">{p.desc}</p>
              <Link to={p.to} data-testid={`vk-pillar-cta-${p.key}`}
                    className={`mt-6 inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white shadow-sm transition-colors ${p.btn}`}>
                {p.cta} <ArrowRight size={12} />
              </Link>
              <DocPanel scopeKey={p.docs} testid={`vk-docs-${p.key}`} />
            </div>
          </div>
        ))}
      </div>

      <div data-testid="viveka-restricted-banner"
           className="flex items-start gap-4 rounded-lg border-l-[3px] border-navy/70 bg-navy/[0.04] px-5 py-4">
        <Lock size={16} className="mt-0.5 text-navy/70" />
        <div>
          <p className="text-sm font-medium text-navy">Family records stay within policy</p>
          <p className="mt-1 card-body">
            Charter history and family records are never surfaced in notifications or exports beyond
            the confidentiality policy set for this engagement.
          </p>
        </div>
      </div>
    </div>
  );
}
